import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Card } from '@deckplan.io/common/cards';
import { Query } from '@deckplan.io/common/filters';
import { MatPaginator } from '@angular/material/paginator';
import { AvailableColumn, SimpleTableComponent } from '../../common/simple-table/simple-table.component';
import { merge, of } from 'rxjs';
import { SortDirection } from '@angular/material/sort';
import { EditableDataField } from '../../common/filter/editable-data-field';
import { InventoryCard } from '@deckplan.io/common/inventory';
import { AdvancedSearchComponent } from '../../common/advanced-search/advanced-search.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { CommonColumns } from '../../decks/common/common-columns';

export interface PageChanges {
    sort: { active: string; direction: SortDirection };
    page: { pageIndex: number; pageSize: number };
}

/* eslint-disable @typescript-eslint/member-ordering */

@Component({
    selector: 'app-card-table',
    templateUrl: './card-table.component.html',
    styleUrls: ['./card-table.component.scss']
})
export class CardTableComponent implements OnInit, AfterViewInit {

    @Output() public cardClicked: EventEmitter<Card> = new EventEmitter<Card>();
    @Output() public cardContextClicked: EventEmitter<{ event: MouseEvent; data: Card }> = new EventEmitter<{ event: MouseEvent; data: Card }>();
    @Output() public newQuery: EventEmitter<Query> = new EventEmitter<Query>();
    @Output() public pagingChanged: EventEmitter<PageChanges> = new EventEmitter<PageChanges>();
    @Output() public cardInputFieldChanged: EventEmitter<{ value: string; data: Card | InventoryCard; dataField: EditableDataField }> =
        new EventEmitter<{ value: string; data: Card | InventoryCard; dataField: EditableDataField }>();
    @Output() public showOwnedCardsSelectorChanged: EventEmitter<MatSlideToggleChange> = new EventEmitter<MatSlideToggleChange>();

    @Input() public columns: AvailableColumn[] = CommonColumns;
    @Input() public tableId = 'Common';

    // The provided query will be AND'd with any
    // user defined queries
    @Input() public cards: Card[] = [];
    @Input() public isLoadingResults = false;
    @Input() public totalSize;
    @Input() public hidePaginator = false;
    @Input() public hideSearch = false;
    @Input() public showDragHint = false;

    @Input() public showOwnedCardsSelector = false;

    @Input() public isValid: (Card) => boolean = () => true;

    @Input() public overrideClick = false;
    @Input() public removableCards: boolean | { deckId: number } = false;
    @Input() public addCardsToDeck: boolean | { deckId: number } = true;
    @Input() public addCardsToInventory = true;
    @Output() public cardActionTaken = new EventEmitter();

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator;
    @ViewChild(SimpleTableComponent, {static: false}) public simpleTable: SimpleTableComponent;
    @ViewChild(AdvancedSearchComponent, {static: false}) public advancedSearch: AdvancedSearchComponent;

    public query: Query = null;

    public pageSizeOptions: number[];
    public isTableView = true;

    // eslint-disable-next-line no-magic-numbers
    public defaultPageSize = 10;
    public defaultPageIndex = 0;
    public defaultSortColumn = 'name';
    public defaultSortDirection: SortDirection = 'asc';

    public columnFields: EditableDataField[] = [];

    // eslint-disable-next-line no-magic-numbers
    private readonly defaultPageSizeOptions = [5, 10, 20, 50, 100];
    // eslint-disable-next-line no-magic-numbers
    private readonly numberOfPageSizeOptions = 5;

    constructor() {
        this.pageSizeOptions = this.defaultPageSizeOptions;
    }

    public static findClosestNumberInArray(array: number[], target: number): number {
        if (target <= array[0]) {
            return array[0];
        } else if (target >= array[array.length - 1]) {
            return array[length - 1];
        }

        // Conduct binary search to see which one is closest
        let i = 0;
        let j = array.length;
        let mid = 0;
        while (i < j) {
            // eslint-disable-next-line no-magic-numbers
            mid = Math.floor(i + j / 2);
            if (array[mid] === target) {
                return array[mid];
            }

            // Search the left half
            if (target < array[mid]) {
                // If the target is greater than the previous to mid, then pick the closer
                if (mid > 0 && target > array[mid - 1]) {
                    return CardTableComponent.getCloserNumber(array[mid - 1], array[mid], target);
                }

                j = mid;
            } else {
                // Search the right half
                if (mid < array.length - 1 && target < array[mid + 1]) {
                    return CardTableComponent.getCloserNumber(array[mid], array[mid + 1], target);
                }

                i = mid + 1;
            }
        }

        // Only one element left after the search
        return array[mid];
    }

    public static getCloserNumber(lower: number, higher: number, target: number): number {
        if (target - lower >= higher - target) {
            return higher;
        } else {
            return lower;
        }
    }

    public ngOnInit() {
        this.columnFields = this.columns.map(c => c.field);
    }

    public ngAfterViewInit(): void {
        merge(
            this.simpleTable ? this.simpleTable.sortChange : of({}),
            this.paginator ? this.paginator.page : of({}),
        ).subscribe(() => {
            const pagination = {
                page: {
                    pageIndex: this.paginator ? this.paginator.pageIndex : this.defaultPageIndex,
                    // eslint-disable-next-line no-magic-numbers
                    pageSize: this.paginator ? this.paginator.pageSize : this.defaultPageSize,
                },
                sort: {
                    active: this.simpleTable?.sort.active ? this.simpleTable.sort.active : this.defaultSortColumn,
                    direction: this.simpleTable?.sort.direction ? this.simpleTable.sort.direction : this.defaultSortDirection
                },
            };
            this.pagingChanged.emit(pagination);
        });
    }

    public onCardViewSelectorChanged() {
        this.isTableView = !this.isTableView;
        if (this.isTableView && !this.hidePaginator) {
            // Try to keep the user's page size as close as we ca n when they switch to the tableview.
            const newPageSize = Math.min(this.defaultPageSize, CardTableComponent.findClosestNumberInArray(this.defaultPageSizeOptions, this.paginator.pageSize));
            this.setPageSize(newPageSize);
            this.pageSizeOptions = this.defaultPageSizeOptions;
        }
    }

    public onCardGridColumnsSet(columnCount: number) {
        // eslint-disable-next-line no-magic-numbers
        this.setPageSize(columnCount * 2);

        // Calculate new page sizes that fit the number of columns that can fit on the user's screen.
        // Ideally, we would like our data retrievals to be in multiples of that column count to fill
        // all rows all the way.
        const newPageSizeOptions = [];
        for (let i = 1; i <= this.numberOfPageSizeOptions; i++) {
            // eslint-disable-next-line no-magic-numbers
            if (i > this.numberOfPageSizeOptions - 2) {
                // Make the larger pages notably larger. We also want them rounded to a multiple of the
                // column size
                // eslint-disable-next-line no-magic-numbers
                let candidateSize = newPageSizeOptions[i - 2] * 2;
                const remainder = candidateSize % columnCount;
                if (remainder !== 0) {
                    candidateSize = candidateSize + columnCount - remainder;
                }
                newPageSizeOptions.push(candidateSize);
            } else {
                newPageSizeOptions.push(i * columnCount);
            }
        }


        // This will try to change the view WHILE we are rendering
        // Wrapping it in a timeout will allow the render cycle to complete first
        setTimeout(() => {
            this.pageSizeOptions = newPageSizeOptions;
        });
    }

    private setPageSize(pageSize: number) {
        if (this.hidePaginator || !this.paginator) {
            return;
        }
        this.paginator.pageSize = pageSize;
        // This hack is required due to the fact that the paginator won't emit a page
        // even when you change the page size. So, we'll do it for it.
        this.paginator.page.emit({
            length: this.paginator.getNumberOfPages(),
            pageIndex: this.paginator.pageIndex,
            pageSize: this.paginator.pageSize
        });
    }

}
